import axios from 'axios';
import Paths from '../config/Paths.js';
import Oauth from './Oauth.js';
import { Buffer } from 'buffer';

import SessionConstants, { SESSION_EXPIRED_EVENT } from '../config/SessionConstants.js';

const Ed3nConfig = __ED3N_CONFIG__;
const SamsConfig = __SAMS_CONFIG__;

axios.defaults.baseURL = __API__;

//  Set the axios interceptor. This injects the authentication header and other required headers into every request
axios.interceptors.request.use(async (config) => 
    {
        console.log('An interceptor is running');
        //  Set the authorization header
        if (config.url != SamsConfig.tokenUrl && config.url != SamsConfig.authUrl && !config.url.startsWith(SamsConfig.validateUrl)) {
            //  we're hitting the ED3N token endpoint. Set the authorization header based on whether we're 
            //  using SAMS or dev login
            if (sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true') {
                const authToken = Buffer.from(sessionStorage.getItem(SessionConstants.DEVLOGIN_USERNAME) + ":" + 
                    sessionStorage.getItem(SessionConstants.DEVLOGIN_PASSWORD)).toString('base64');
                config.headers.Authorization = 'Basic ' + authToken;
            } else {
                const samsToken = sessionStorage.getItem(SessionConstants.SAMS_ACCESS_TOKEN);
                config.headers.Authorization = 'Bearer ' + samsToken;
            }
        }
        
        return config;
    },
    error => Promise.reject(error)
);

axios.interceptors.response.use(
    function (response) {
        return response;
    },
    async function (error) {
        // --- TEST ONLY: 
        // error.response = undefined; // <<-- this is to simulate an error response for testing purposes
        // --------------------------------------------------------------
        const originalRequest = error.config || {};
        const requestUrl = originalRequest.url || '';

        //  A well-formed response error always carries its request config. Without it we
        //  cannot safely retry (axios would replay an empty request) and must not risk
        //  falsely signalling an expired session, so reject as-is.
        if (!error.config) {
            return Promise.reject(error);
        }

        //  Never attempt refresh/retry against the auth endpoints themselves. A 401 from
        //  the token endpoint means the refresh itself failed, and retrying it would recurse.
        //  The validate endpoint (Paths.samsValidate) is likewise excluded so a 401 there
        //  surfaces as "invalid" rather than being silently refreshed-and-retried behind the
        //  caller's back.
        const isAuthEndpoint = requestUrl === SamsConfig.tokenUrl
            || requestUrl === SamsConfig.authUrl
            || requestUrl.startsWith(Paths.samsValidate);

        if (error.response && error.response.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
            if (sessionStorage.getItem(SessionConstants.DEVLOGIN) === 'true') {
                console.log('Caught invalid DEV login credentials');
                sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'Dev Login credentials are invalid.');
                window.location.assign('/error');
                return Promise.reject(error);
            } else {
                originalRequest._retry = true;

                //  Await the refresh (single-flighted inside Oauth) so the request interceptor
                //  picks up the NEW token on retry. Firing it without awaiting replays the
                //  request with the old expired token, which always 401s again.
                const refreshed = await Oauth.refreshSamsToken();

                if (refreshed.ok) {
                    //  Drop the stale header so the request interceptor re-injects the new token.
                    if (originalRequest.headers) delete originalRequest.headers.Authorization;
                    return axios(originalRequest);
                }

                //  Only a rejected grant proves the session is over. A transient failure
                //  (network, 5xx) must not be reported to the user as an expired session,
                //  so surface it as an error and let the caller handle the 401 normally.
                if (refreshed.reason === 'rejected') {
                    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
                } else {
                    console.error('[APIUtils] Could not refresh the SAMS token after a 401.', requestUrl);
                }
                return Promise.reject(error);
            }
        } else if (!error.response) {
            // Network error — the API is unreachable, so the app cannot load at all. This is the
            // only case that redirects to the shared Error page. Server errors (5xx) and other
            // statuses are rejected here and handled inline by each caller so a single failing
            // endpoint doesn't look like a whole-application failure.
            sessionStorage.setItem(SessionConstants.ERROR_MESSAGE, 'A network error occurred');
            sessionStorage.setItem(SessionConstants.ERROR_DESCRIPTION, 'The server could not be reached. Please try again later.');
            sessionStorage.setItem(SessionConstants.TIMEOUT_STATUS, false);
            window.location.assign('/error');
            return Promise.reject(error);
        } else {
            return Promise.reject(error);
        }
    }
);

const APIUtils = {

    /*  ========================================
     *  ============ OAUTH ENDPOINTS ===========
     *  ======================================== */

    /*  SAMS TOKEN ENDPOINTS */

    async getSamsTokens(code) {
        let url = Paths.samsToken;
        var formData = new URLSearchParams();
        formData.append('grant_type', 'authorization_code');
        formData.append('code', code);
        formData.append('redirect_uri', SamsConfig.redirectUri);
        formData.append('client_id', SamsConfig.clientId);
        formData.append('client_secret', SamsConfig.clientSecret);

        var response = await axios.post(url, formData);
        var tokenData = {
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
            expiresIn: response.data.expires_in,
        };
        return tokenData;
    },

    async refreshSamsTokens(token) {
        let url = Paths.samsToken;
        var formData = new URLSearchParams();
        formData.append('grant_type', 'refresh_token');
        formData.append('refresh_token', token);
        formData.append('redirect_uri', SamsConfig.redirectUri);
        formData.append('client_id', SamsConfig.clientId);
        formData.append('client_secret', SamsConfig.clientSecret);

        var response = await axios.post(url, formData);
        var tokenData = {
            accessToken: response.data.access_token,
            refreshToken: response.data.refresh_token,
            expiresIn: response.data.expires_in,
        };
        return tokenData;
    },

    async getSamsUserInfo() {
        let url = Paths.samsUserInfo;

        var response = await axios.get(url);
        return response.data;
    },

    async getEd3nUserInfo(userId) {
        let url = Paths.getUser.replace('{id}', userId.replace('@', '%40'));

        var response = await axios.get(url);
        return response.data;
    },

    async verifyLogin() {
        let url = Paths.authValidate;

        var response = await axios.post(url);
        return response.data;
    },

    /*  ========================================
     *  ======= TASK MANAGEMENT ENDPOINTS ======
     *  ======================================== */
    async getAlerts() {
        let url = Paths.alert;

        var response = await axios.get(url);
        return response.data;
    },

    /*  ========================================
     *  ========= DATASTORE ENDPOINTS ==========
     *  ======================================== */
    async getDatastoreVariant(variantId) {
        let url = Paths.datastoreGetVariant.replace('{variantId}', variantId);

        var response = await axios.get(url);
        return response.data;
    },
   async getHgvsNames(variantId) {
        let url = Paths.datastoreGetHgvsNames.replace('{variantId}', variantId);

        var response = await axios.get(url);
        return response.data;
    },

    /*  ========================================
    *   ========= EXPLORE ENDPOINTS ==========
    *   ======================================== */
    async getBiochemicalPrograms() {
        let url = Paths.getBiochemicalPrograms;
        var response = await axios.get(url);
        return response.data;
    },
    
    async getBiochemicalAnalytes() {   
        let url = Paths.getBiochemicalAnalytes; 
        var response = await axios.get(url);
        return response.data;
    },

    async getBiochemicalScatterplotAxisOptions() {
        let url = Paths.getBiochemicalScatterplotAxisOptions;
        var response = await axios.get(url);
        return response.data;
    },

    async getConditionsLookup() {
        let url = Paths.getConditionsLookup;
        var response = await axios.get(url);
        return response.data;
    },

    async getAnalytesLookup() {
        let url = Paths.getAnalytesLookup;
        var response = await axios.get(url);
        return response.data;
    },

    async getTags() {
        let url = Paths.getTags;
        var response = await axios.get(url);
        return response.data;
    },

    async searchVariant(body, programId) {
        let url = Paths.searchVariant.replace('{programId}', programId);
        var response = await axios.post(url, body);
        return response.data;
    },

    async searchGenotype(programId, variantObjectIds) {
        let url = Paths.searchGenotype.replace('{programId}', programId);
        var response = await axios.post(url, variantObjectIds);
        return response.data;
    },

    async getGenesLookup() {
        let url = Paths.lookupGenes;
        var response = await axios.get(url);
        return response.data;
    },

    /*  ========================================
    *   ========= BIOCHEMICAL ENDPOINTS ==========
    *   ======================================== */
    async getBiochemicalStatistics(programId = null, analyteCode = null) {
        let url = Paths.getBiochemicalStatistics;
        const params = {};

        // Only add programId if not null/undefined (accepts comma-separated values)
        if (programId) params.programId = programId;
        // Only add analyteCode if not null/undefined (single value only)
        if (analyteCode) params.analyteCode = analyteCode;

        var response = await axios.get(url, { params });
        return response.data;
    },

    async getBiochemicalDataByProgramPatientId(programId, patientId, testId = 0) {
        let url = Paths.getBiochemicalByProgramPatientId
            .replace('{programId}', programId)
            .replace('{patientId}', patientId)
            .replace('{testId}', testId);
        var response = await axios.get(url);
        return response.data;
    },

    async biochemicalScatterplot(axisOptions) {
        let url = Paths.biochemicalScatterplot;
        const params = new URLSearchParams();
        params.append('xAxis', axisOptions.xAxis);
        params.append('yAxis', axisOptions.yAxis);
        params.append('yAxisType', axisOptions.yAxisType);
        params.append('programId', axisOptions.programId);
        if (axisOptions.patientId != null) {
            params.append('patientId', axisOptions.patientId);
        }

        var response = await axios.post(url, params);
        return response.data;
    },

    async biochemicalHistogram(histogramOptions) {
        let url = Paths.biochemicalHistogram;
        const params = new URLSearchParams();
        params.append('xAxis', histogramOptions.analyte);
        params.append('programId', histogramOptions.programId);
        params.append('patientId', histogramOptions.patientId);

        var response = await axios.post(url, params);
        return response.data;
    },

    /*  ========================================
    *   ========= MOLECULAR ENDPOINTS ==========
    *   ======================================== */

    /*  PROGRAM SUMMARY ENDPOINTS */

    async getVariantsByProgramId(programId) {
        let url = Paths.getVariantSummaryByProgramId.replace('{programId}', programId);
        var response = await axios.get(url);
        return response.data;
    },

    async getVariantSummaryUserColumns() {
        var response = await axios.get(Paths.userHgvsColumns);
        return response.data;
    },

    async saveVariantSummaryUserColumns(columns) {
        var response = await axios.post(Paths.userHgvsColumns, columns);
        return response.data;
    },

    async getVariantsByProgramPatientId(programId, patientId) {
        let url = Paths.getVariantSummaryByProgramPatientId.replace('{programId}', programId).replace('{patientId}', patientId);

        var response = await axios.get(url);
        return response.data;
    },

    async getPatientsByProgramId(programId) {
        let url = Paths.getPatientSummaryByProgramId.replace('{programId}', programId);

        var response = await axios.get(url);
   
        return response.data;
    },

    /*  DATA UPLOADING ENDPOINTS */

    async uploadMolecularFile(formData) {
       let url = Paths.molecularFileUpload;     
    //    console.log("URL", url)
       var response = await axios.post(url, formData);
       return response.data;
    },

    async uploadBiochemicalFile(formData) {
        let url = Paths.biochemicalFileUpload;
        // console.log("URL", url)
        var response = await axios.post(url, formData);
        return response.data;
    },

    async uploadClinicalFile(formData) {    
        let url = Paths.clinicalFileUpload;
        // console.log("URL", url)
        var response = await axios.post(url, formData);
        return response.data;
    },

     /* METADATA ENDPOINTS */
     async getMetadataValuesByField(fieldName) {
        let url = Paths.molecularGetMetadata.replace('{fieldName}', fieldName);
        var response = await axios.get(url);
        return response.data;
    },


    /*  REVIEW ENDPOINTS */
    async getReview(programId, variantId) {
        let url = Paths.molecularGetReview.replace('{programId}', programId).replace('{variantId}', variantId);

        var response = await axios.get(url);
        return response.data;
    },

    async getProgramActivity(programId, variantId) {
        let url = Paths.molecularGetProgramActivity.replace('{programId}', programId).replace('{variantId}', variantId);

        var response = await axios.get(url);
        return response.data;
    },
    
    async deleteReview(formData) {
        return this.getResponseData(Paths.molecularDeleteReview,formData);
    },

    async createReview(formData) {
        return this.getResponseData(Paths.molecularCreateReview,formData);
    },

    async reopenReview(formData) {
        return this.getResponseData(Paths.molecularReopenReview,formData);
    },

    /*  INTERPRETATION ENDPOINTS */
    async getInterpretationsAssignedToUser() {
        let url = Paths.molecularGetInterpretationsAssignedToUser.replace('{userId}', sessionStorage.getItem(SessionConstants.USER_EMAIL));

        var response = await axios.get(url);
        return response.data;
    },
    
    async getVariantInterpretation(userId, variantId) {
        let url = Paths.molecularGetInterpretation.replace('{programId}', sessionStorage.getItem(SessionConstants.PROGRAM_ID))
                .replace('{userId}', userId).replace('{variantId}', variantId);

        var response = await axios.get(url);
        return response.data;
    },

    async getInterpretationsByReview(variantReviewId) {
        let url = Paths.molecularGetInterpretationsByReview.replace('{variantReviewId}', variantReviewId);

        var response = await axios.get(url);
        return response.data;
    },

    async addVariantInterpretation(programId, variantId,formData) {
        let url = Paths.molecularAddVariantInterpretation.replace('{programId}', programId).replace('{variantId}', variantId);
        await axios.post(url, formData).then((response) => {
            return response.data;
        }).catch((error) => {
            console.log("Unable to add variant interpretation. The operation returned an error.");
            return null;
        });
    },

    async updateVariantInterpretation(programId, variantId, formData) {
        let url = Paths.molecularUpdateVariantInterpretation.replace('{programId}', programId).replace('{variantId}', variantId);
        var response = await axios.post(url, formData);
        return response.data;
    },

    async assignInterpretation(reviewId, formData) {
        let url = Paths.molecularAssignVariantInterpretation.replace('{variantReviewId}', reviewId);

        var response = await axios.post(url, formData);
        return response.data;
    },

    async unassignInterpretation(reviewId, formData) {
        let url = Paths.molecularUnassignVariantInterpretation.replace('{variantReviewId}', reviewId);

        var response = await axios.post(url, formData);
        return response.data;
    },
    async createInterpretation(reviewId, formData) {
        let url = Paths.molecularUnassignVariantInterpretation.replace('{variantReviewId}', reviewId);
        var response = await axios.post(url, formData);
        return response.data;
    },

    /*  CONSENSUS ENDPOINTS */

    async getFinalVariantInterpretations(variantId) {
        let url = Paths.molecularGetFormattedFinalVariantInterpretations.replace('{variantId}', variantId);

        var response = await axios.get(url);
        return response.data;
    },

    async getConsensusSummary(programId, variantId) {
        let url = Paths.molecularGetConsensusSummary.replace('{variantId}', variantId).replace('{programId}', programId);

        var response = await axios.get(url);

        return response.data;
  
    },

    /*  LITERATURE ENDPOINTS */

    async searchLiteratureForVariant(variantId) {
        let url = Paths.molecularSearchLiterature.replace("{variantId}", variantId);

        var response = await axios.get(url);
        return response.data;
    },

    async getLiteratureReviewsForReview(variantInterpretationId) {
        let url = Paths.molecularGetLiteratureReviews.replace("{variantInterpretationId}", variantInterpretationId);

        var response = await axios.get(url);
        return response.data;
    },

    async saveLiteratureReview(variantInterpretationId, formData) {
        let url = Paths.molecularSaveLiteratureReviews.replace('{variantInterpretationId}', variantInterpretationId);

        var response = await axios.post(url, formData);
        return response.data;
    },

    
    async saveFinalConsensusLiteratureReview(formData) {
        var response = await axios.post(Paths.molecularSaveFinalConsensusLiteratureReviews, formData);
        return response.data;
    },
    async deleteLiteratureReview(variantInterpretationId, pmid) {
        let url = Paths.molecularDeleteLiteratureReview.replace("{variantInterpretationId}", variantInterpretationId).replace("{pmid}", pmid);

        await axios.post(url);
    },

    async getLiteratureCategories() {
        let url = Paths.molecularGetLiteratureCategories;

        var response = await axios.get(url);
        return response.data;
    },

    /* ANNOTATION ENDPOINTS */

    async getAnnotationsForVariant(variantId) {
        let url = Paths.datastoreGetAnnotations.replace('{variantId}', variantId);

        var response = await axios.get(url);
        // console.log("annotations for variant: " + response.data);
        return response.data;       
    },
    async getFieldDescription() {
        var response = await axios.get(Paths.datastoreGetFieldDescriptions);
        return response.data;
    },
    /*  ===============================
     *  ===== REPORTING ENDPOINTS =====
     *  =============================== */

    async getPatientReport(programId, patientId) {
        let url = Paths.getPatientReport.replace('{programId}', programId).replace('{patientId}', patientId);
        let headers = {
            'Content-Type': 'application/xml',
            'Accept': 'application/xml'
        };

        var response = await axios.get({
            url: url,
            headers: headers,
        });
        return response.data;
    },

    /*  ===============================
     *  ====== SETTINGS ENDPOINTS =====
     *  =============================== */

    async getProgramSettings(programId) {
        let url = Paths.getProgram.replace("{programId}", programId);

        var response = await axios.get(url);
        return response.data;
    },

    async updateProgramSettings(programId, formData) {
        let url = Paths.updateProgramSetting.replace('{programId}', programId);

        var response = await axios.post(url, formData);
        return response.data;
    },

    async getRestrictedConditions(programId) {
        let url = Paths.getRestrictedCondition.replace('{programId}', programId);

        var response = await axios.get(url);
        return response.data;
    },

    async getProgramSettingHistory(programId, params) {
        let url = Paths.getProgramSettingHistory.replace('{programId}', programId);

        // axios omits undefined params, so callers can pass only the filters they need
        var response = await axios.get(url, { params });
        return response.data;
    },

    async updateRestrictedConditions(programId, restrictedConditions) {
        let url = Paths.updateRestrictedCondition.replace('{programId}', programId);

        var response = await axios.post(url, restrictedConditions);
        return response.data;
    },

    async getCutoffValues(programId) {
        let url = Paths.getCutoffValues.replace('{programId}', programId);

        var response = await axios.get(url);
        return response.data;
    },

    async updateCutoffValues(programId, cutoffValues) {
        let url = Paths.updateCutoffValues.replace('{programId}', programId);

        var response = await axios.post(url, cutoffValues);
        return response.data;
    },

    async validateCutoffImport(programId, formData) {
        let url = Paths.validateCutoffImport.replace('{programId}', encodeURIComponent(programId));
        var response = await axios.post(url, formData);
        return response.data;
    },

    async updateReviewSettings(reviewId, formData) {
        let url = Paths.molecularUpdateReviewSetting.replace('{variantReviewId}', reviewId);

        var response = await axios.post(url, formData);
        return response.data;
    },

    async getProgramUsers(programId, activeOnly=true) {
        let url = Paths.getProgramUsers.replace('{programId}', programId);
        if (!activeOnly) {
            url += "?activeOnly=false";
        }

        var response = await axios.get(url);
        return response.data;
    },
     // **** Helper **************//  
    async getResponseData(url,formData=null){
        if (formData ==null)
             return await axios.get(url).data;
        else
             return (await axios.post(url,formData)).data;
    },

    /*  ===============================
     *  ====== WORKFLOW ENDPOINTS =====
     *  =============================== */

    async validateVCFFile(formData) {
        let url = Paths.validateVCFFile.replace("{programId}", formData.get('programId'));;     
        var response = await axios.post(url, formData);
        return response.data;
     },

    async validateBiochemicalCSVFile(formData) {
        let url = Paths.validateBiochemicalCSVFile.replace("{programId}", formData.get('programId'));
        var response = await axios.post(url, formData);
        return response.data;
    },         

    async validateClinicalFile(file, programId) {
        let url = Paths.validateClinicalFile.replace("{programId}", encodeURIComponent(programId));
        const formData = new FormData();
        formData.append('file', file);
        var response = await axios.post(url, formData);
        return response.data;
    },

    async getAvailableWorkflows() {
        let url = Paths.getAvailableWorkflows;

        var response = await axios.get(url);
        return response.data;
    },

    async getWorkflowRunsByProgramId(programId) {
        let url = Paths.getWorkflowByProgramId.replace("{programId}", programId);

        var response = await axios.get(url);
        return response.data;
    },

    async getWorkflowStepsByWorkflowRunId(workflowRunId) {
        let url = Paths.getWorkflowStepsByWorkflowRunId.replace("{workflowRunId}", workflowRunId);

        var response = await axios.get(url);
        return response.data;
    },
    /*  ===============================
     *  ====== SIGNALR =====
     *  =============================== */

    async signalRnegotiate() {
        var response = await axios.post(Paths.signalRnegotiateUrl,
            { UserId: sessionStorage.getItem(SessionConstants.USER_EMAIL) }
        );
        return response.data;
     },
    /*  ===============================
     *  ====== User Management =====
     *  =============================== */

    async getAllUsers(activeOnly = true) {
        var response = activeOnly ? await axios.get(Paths.getAllUsers) : await axios.get(Paths.getAllUsers + "?activeOnly=false");
        return response.data;
     },

    async createUser(userData) {
        var response = await axios.post(Paths.createUser, userData);
        return response.data;
    },

    async updateUser(userData) {
        var response = await axios.post(Paths.updateUser, userData);
        return response.data;
    },

    async updateUserStatus(email, isActive) {
        let url = Paths.updateUserStatus.replace('{userId}', email) + `&active=${isActive}`;

        var response = await axios.post(url);
        return response.data;
    },
     /*  ===============================
     *  ====== Lookup =====
     *  =============================== */
    async lookupPrograms() {
        var response = await axios.get(Paths.lookupPrograms);
        return response.data;
    },
    async lookupRoles() {
        var response = await axios.get(Paths.lookupRoles);
        return response.data;
    },

    /*  ===============================
     *  ====== Tags =====
     *  =============================== */
    async getTags() {
        var response = await axios.get(Paths.getTags);
        return response.data;
    }
}

export default APIUtils;